import { Button, Tooltip } from "@mantine/core";
import { DateTimePicker, MonthPickerInput } from "@mantine/dates";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CalendarIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import "dayjs/locale/tr";
import styled from "styled-components";
import CountingButton from "../common/Buttons/CountingButton";
import { theme } from "../../theme";

/**
 * İki rapor ekranının üst filtre şeridi.
 *
 * Daha önce her iki tabloda ayrı ayrı, inline `style` ile kuruluyordu; alan
 * etiketleri satır kaydırınca inputlar birbiriyle hizasını kaybediyor,
 * butonlar da panelin buton bileşenlerine benzemiyordu. Tek bileşende
 * toplandı: alanlar ortak etiket/kutu sarmalayıcısını, butonlar panelin
 * mor/beyaz ikilisini kullanıyor. "Buffer Time" başlıkları da Türkçeleşti —
 * alanın birimi dakika.
 *
 * Tarih ve tolerans değerleri "Güncelle"ye basılana kadar isteğe gitmez —
 * ay değiştirirken her tıklamada rapor yeniden hesaplanmasın diye kasıtlı.
 */
interface Props {
  /** Aylık raporda ay seçici, giriş/çıkış raporunda tarih-saat seçici. */
  mode: "month" | "datetime";
  startDate: string | null;
  endDate: string | null;
  onStartDateChange: (value: string | null) => void;
  onEndDateChange: (value: string | null) => void;
  entryBuffer: number;
  exitBuffer: number;
  entryCounter: { increment: () => void; decrement: () => void; set: (value: number) => void };
  exitCounter: { increment: () => void; decrement: () => void; set: (value: number) => void };
  onApply: () => void;
  onExport: () => void;
  applying?: boolean;
  exporting?: boolean;
}

const ReportFilterBar = ({
  mode,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  entryBuffer,
  exitBuffer,
  entryCounter,
  exitCounter,
  onApply,
  onExport,
  applying = false,
  exporting = false,
}: Props) => {
  const datePlaceholder =
    mode === "month" ? "Ay seçiniz" : "Tarih ve saat seçiniz";

  const renderDateField = (
    value: string | null,
    onChange: (value: string | null) => void
  ) => {
    const shared = {
      locale: "tr",
      placeholder: datePlaceholder,
      leftSection: <CalendarIcon height={20} />,
      leftSectionPointerEvents: "none" as const,
      value,
      onChange: onChange as any,
      popoverProps: { withinPortal: true },
    };

    return mode === "month" ? (
      <MonthPickerInput {...shared} />
    ) : (
      <DateTimePicker {...shared} clearable />
    );
  };

  return (
    <Card>
      <IconWrap>
        <FunnelIcon width={18} />
      </IconWrap>

      <Fields>
        <DateFieldBox>
          <FieldLabel>Başlangıç</FieldLabel>
          {renderDateField(startDate, onStartDateChange)}
        </DateFieldBox>

        <DateFieldBox>
          <FieldLabel>Bitiş</FieldLabel>
          {renderDateField(endDate, onEndDateChange)}
        </DateFieldBox>

        <CountingButton
          label="Giriş Toleransı (dk)"
          description="Vardiya başlangıcından sonraki bu kadar dakika 'geç' sayılmaz."
          value={entryBuffer}
          increment={entryCounter.increment}
          decrement={entryCounter.decrement}
          setValue={entryCounter.set}
        />
        <CountingButton
          label="Çıkış Toleransı (dk)"
          description="Vardiya bitişinden önceki bu kadar dakika 'erken çıkış' sayılmaz."
          value={exitBuffer}
          increment={exitCounter.increment}
          decrement={exitCounter.decrement}
          setValue={exitCounter.set}
        />
      </Fields>

      <Actions>
        <SecondaryButton
          variant="default"
          radius={10}
          leftSection={<ArrowPathIcon width={18} />}
          loading={applying}
          onClick={onApply}
        >
          Güncelle
        </SecondaryButton>
        <Tooltip label="Filtrelenen tüm satırları Excel olarak indirir" withArrow>
          <PrimaryButton
            radius={10}
            color={theme.colors.brand.normal}
            leftSection={<ArrowDownTrayIcon width={18} />}
            loading={exporting}
            onClick={onExport}
          >
            İndir
          </PrimaryButton>
        </Tooltip>
      </Actions>
    </Card>
  );
};

export default ReportFilterBar;

const Card = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  padding: 16px;
  margin-bottom: 20px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.02);
  flex-wrap: wrap;
`;

// Etiket yüksekliği kadar aşağıda: alanlar dar ekranda alt satıra kaydığında
// ikon ilk inputla aynı hizada kalsın.
const IconWrap = styled.div`
  width: 44px;
  height: 44px;
  margin-top: 24px;
  flex-shrink: 0;
  background: ${theme.colors.brand.normal}15;
  color: ${theme.colors.brand.normal};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Fields = styled.div`
  display: flex;
  align-items: flex-end;
  row-gap: 14px;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
`;

// Tarih alanları 190px: eski 150px'te "Rapor Başlangıç Tarihi" etiketi iki
// satıra kayıyor, yanındaki alanlarla hizası bozuluyordu.
const DateFieldBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 190px;

  .mantine-DateTimePicker-input,
  .mantine-MonthPickerInput-input {
    height: 44px;
    border-radius: 10px;
  }

  .mantine-Input-section svg {
    color: ${theme.colors.brand.normal};
  }
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #64748b;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  align-self: flex-end;
`;

const SecondaryButton = styled(Button)<any>`
  height: 44px;
  border-color: ${theme.colors.brand.normal};
  color: ${theme.colors.brand.normal};

  &:hover {
    background: #f1eaff;
  }
`;

const PrimaryButton = styled(Button)<any>`
  height: 44px;
`;
